import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper
} from '@mui/material';
import { useEmployeeSelectionColumns } from './columns';
import { useListEmployees } from '../../../../queries/employee';
import { useStore } from '../../../../hooks/useStore';
import { useMemo, useState } from 'react';
import Pagination from '../shared/pagination';
import { DEFAULT_PAGE_SIZE, scrollableTableContainerSx } from '../shared/utils';
import { cn } from '../../../util';
import { useTranslation } from 'react-i18next';
import { EmployeeListItem } from '@pxtra-client-lib-legacy/gen';
import { DataTableLoadingBar } from '../shared/loading-bar';
import { observer } from 'mobx-react-lite';
import { SortableTableHeader } from '../shared/sortable-header';

export const EmployeeSummaryTable = observer(() => {
  const { companyStore, exportFlowStore } = useStore();
  const { t } = useTranslation();

  const { data, isFetching } = useListEmployees(companyStore.company?.id, {
    enabled: !!companyStore.company?.id
  });
  const employeesData = useMemo(() => {
    return (
      data?.pages
        ?.flatMap((page) => page)
        .filter(
          (employee) =>
            (
              exportFlowStore.data as { employees_query?: { ids?: string[] } }
            ).employees_query?.ids?.includes(employee.id ?? '') ?? true
        ) || []
    );
  }, [data, exportFlowStore.data]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });
  const columns = useEmployeeSelectionColumns(true);

  const table = useReactTable({
    data: employeesData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination
    }
  });

  const isMissingId = (row: EmployeeListItem) =>
    exportFlowStore.currentType === 'payroll_data' && !row.employee_number;

  return (
    <Paper className="overflow-hidden flex flex-col rounded-none">
      <TableContainer sx={scrollableTableContainerSx('calc(100vh - 575px)')}>
        <Table stickyHeader>
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <SortableTableHeader
                    key={header.id}
                    header={header}
                    isLoading={isFetching}
                    hasData={employeesData.length > 0}
                  />
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell
                sx={{
                  padding: 0,
                  borderBottom: 'none',
                  backgroundColor: 'transparent'
                }}
                colSpan={columns.length}
              >
                <DataTableLoadingBar isLoading={isFetching} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn(
                  'relative',
                  isMissingId(row.original as EmployeeListItem) && [
                    'before:absolute before:content-[attr(data-missing-id-text)] before:w-full before:text-center before:leading-[60px] before:font-medium before:text-error-main before:bg-[#ffe6f0]',
                    '[&>*]:opacity-25 [&>*:not([data-field="employee"])]:blur-sm'
                  ]
                )}
                data-missing-id-text={
                  isMissingId(row.original as EmployeeListItem)
                    ? t('employee:ID_MISSING')
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell className="py-2 border-grey-100" key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination table={table} />
    </Paper>
  );
});
